import React, { useState, useMemo, useEffect } from 'react';
import { confirmDialog } from '../lib/confirm';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../../config/axios';
import { Meta, Display, Chip, Button } from '../components/Atoms';
import { LoadingState, ErrorState, EmptyState } from '../components/states';
import { useApi } from '../lib/api';
import { formatLocalDate } from '../i18n';

/**
 * Photo View - single photo z glassmorphic info sheet u dołu.
 *
 * Z paczki photo.jsx (ScreenPhotoView).
 *
 * - Full-bleed photo z gradient darkening (góra + dół)
 * - Top bar (transparent z blur): close + numer/data + more
 * - Bottom sheet (glassmorphic z blur): tag + tytuł + opis + chips + akcja
 * - Swipe dots (na środku-dole) - nawigacja między zdjęciami z galerii
 * - Klawisze ←/→ na desktopie
 *
 * Endpoint:
 *   /api/gallery - pobiera całą galerię, wybiera fotkę po :id i pokazuje
 *   navigation context (poprzednie/następne)
 */

function imageUrl(path) {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  if (path.startsWith('/')) return path;
  return `/${path}`;
}

function tagFor(photo) {
  if (photo.tag && photo.tag !== 'ogólne') return photo.tag;
  if (photo.source_type === 'progress') return 'Postęp';
  if (photo.source_type === 'harvest') return 'Zbiór';
  return null;
}

// ── Modal edycji podpisu ────────────────────────────────────────
function EditCaptionModal({ open, photo, onClose, onSaved, onDeleted }) {
  const [caption, setCaption] = useState('');
  const [takenDate, setTakenDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && photo) {
      setCaption(photo.caption || '');
      setTakenDate((photo.taken_date || photo.created_at || '').slice(0, 10));
      setError('');
    }
  }, [open, photo]);

  if (!open || !photo) return null;

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.put(`/api/gallery/${photo.id}`, {
        caption: caption.trim() || null,
        taken_date: takenDate || null,
      });
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udało się zapisać.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!await confirmDialog('Usunąć to zdjęcie z galerii? Akcja nieodwracalna.')) return;
    setSaving(true);
    setError('');
    try {
      await axios.delete(`/api/gallery/${photo.id}`);
      onDeleted?.();
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udało się usunąć.');
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog" aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(15, 20, 16, 0.85)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--t-bg)',
          border: '1px solid var(--t-line)',
          borderRadius: 16,
          maxWidth: 480, width: '100%',
          padding: 24,
        }}
      >
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 20,
        }}>
          <Display size={22} style={{ display: 'block' }}>Edytuj zdjęcie</Display>
          <button
            type="button" onClick={onClose}
            style={{
              background: 'transparent', border: 'none',
              color: 'var(--t-text-mute)', cursor: 'pointer',
              fontSize: 22, lineHeight: 1, padding: 0,
            }}
          >×</button>
        </div>

        {error && (
          <div style={{
            padding: '10px 14px', borderRadius: 10,
            background: 'rgba(192, 106, 74, 0.08)',
            borderLeft: '3px solid var(--t-rust)',
            fontSize: 13, color: 'var(--t-text)', marginBottom: 14,
          }}>{error}</div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={{
            display: 'block', marginBottom: 6,
            fontFamily: 'var(--t-mono)', fontSize: 10,
            letterSpacing: '.22em', textTransform: 'uppercase',
            color: 'var(--t-text-dim)',
          }}>Podpis</label>
          <textarea
            aria-label="Podpis zdjęcia"
            value={caption}
            onChange={e => setCaption(e.target.value)}
            placeholder="Co widać na zdjęciu? Data, kontekst, plan…"
            maxLength={500}
            rows={3}
            style={{
              width: '100%', padding: '12px 14px', borderRadius: 10,
              background: 'var(--t-bg-elev)',
              border: '1px solid var(--t-line-2)',
              color: 'var(--t-text)', fontFamily: 'var(--t-sans)',
              fontSize: 14, letterSpacing: '-.01em',
              outline: 'none', boxSizing: 'border-box',
              resize: 'vertical',
            }}
          />
          <div style={{
            marginTop: 4, fontSize: 11, color: 'var(--t-text-mute)',
            fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
            textAlign: 'right',
          }}>{caption.length} / 500</div>
        </div>

        <div style={{ marginBottom: 22 }}>
          <label style={{
            display: 'block', marginBottom: 6,
            fontFamily: 'var(--t-mono)', fontSize: 10,
            letterSpacing: '.22em', textTransform: 'uppercase',
            color: 'var(--t-text-dim)',
          }}>Data zrobienia (opcjonalna)</label>
          <input
            type="date"
            aria-label="Data zrobienia"
            value={takenDate}
            onChange={e => setTakenDate(e.target.value)}
            style={{
              width: '100%', padding: '12px 14px', borderRadius: 10,
              background: 'var(--t-bg-elev)',
              border: '1px solid var(--t-line-2)',
              color: 'var(--t-text)', fontFamily: 'var(--t-sans)',
              fontSize: 14, outline: 'none', boxSizing: 'border-box',
              colorScheme: 'dark',
            }}
          />
        </div>

        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: 10, paddingTop: 14,
          borderTop: '1px solid var(--t-line)',
        }}>
          <button
            type="button"
            onClick={handleDelete}
            disabled={saving}
            style={{
              padding: '10px 16px', borderRadius: 10,
              background: 'transparent',
              border: '1px solid var(--t-rust)',
              color: 'var(--t-rust)', cursor: saving ? 'wait' : 'pointer',
              fontFamily: 'var(--t-sans)', fontSize: 13,
            }}
          >Usuń zdjęcie</button>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" onClick={onClose} disabled={saving}>Anuluj</Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Zapisuję…' : 'Zapisz'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PhotoView() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const photoId = parseInt(id, 10);

  const { data, loading, error, refetch } = useApi('/api/gallery');
  const photos = Array.isArray(data) ? data : [];
  const [editing, setEditing] = useState(false);

  const currentIndex = useMemo(() => photos.findIndex(p => p.id === photoId), [photos, photoId]);
  const photo = currentIndex >= 0 ? photos[currentIndex] : null;
  const total = photos.length;

  const goPrev = () => {
    if (currentIndex > 0) {
      navigate(`/gallery/${photos[currentIndex - 1].id}`, { replace: true });
    }
  };
  const goNext = () => {
    if (currentIndex < total - 1) {
      navigate(`/gallery/${photos[currentIndex + 1].id}`, { replace: true });
    }
  };

  // Klawisze ← → Esc
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft')  goPrev();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'Escape')     (window.history.length > 1 ? navigate(-1) : navigate('/gallery'));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, photos]);

  if (loading) return <LoadingState label={t('common.loading')} />;
  if (error)   return <ErrorState error={error} />;
  if (!photo) {
    return (
      <EmptyState
        title="Zdjęcie nie znalezione"
        description="Wróć do galerii i wybierz inne."
      />
    );
  }

  const src = imageUrl(photo.medium_path || photo.photo_path || photo.thumb_path);
  const tag = tagFor(photo);
  const dateLabel = formatLocalDate(photo.taken_date || photo.created_at, 'long');
  const timeLabel = (() => {
    const d = new Date(photo.created_at);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  })();

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: '#000',
      zIndex: 1000,
      overflow: 'hidden',
    }}>
      {/* Full-bleed photo + darkening gradient */}
      <div style={{
        position: 'absolute', inset: 0,
        background: src
          ? `url(${src}) center / contain no-repeat, #000`
          : 'linear-gradient(135deg, var(--t-sage) 0%, var(--t-amber) 50%, var(--t-rust) 100%)',
      }} />

      {/* Darkening overlay - góra + dół, środek czysty */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.15) 18%, rgba(0,0,0,0.15) 60%, rgba(0,0,0,0.85) 100%)',
        pointerEvents: 'none',
      }} />

      {/* Top bar */}
      <div style={{
        position: 'absolute', top: 'max(env(safe-area-inset-top, 0), 20px)',
        left: 0, right: 0, zIndex: 20,
        padding: '0 20px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <button
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/gallery'))}
          aria-label={t('common.close')}
          style={{
            width: 40, height: 40, borderRadius: '50%',
            background: 'rgba(0, 0, 0, 0.45)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: 'var(--t-text)',
            fontSize: 18, cursor: 'pointer',
            display: 'grid', placeItems: 'center',
          }}
        >×</button>
        <div style={{
          fontFamily: 'var(--t-mono)', fontSize: 10,
          letterSpacing: '.2em', textTransform: 'uppercase',
          color: 'var(--t-text)', textAlign: 'center',
        }}>
          {currentIndex + 1} z {total}
          <div style={{ fontSize: 8, opacity: 0.6, marginTop: 2 }}>
            {dateLabel} · {timeLabel}
          </div>
        </div>
        <div style={{
          width: 40, height: 40, borderRadius: '50%',
          background: 'rgba(0, 0, 0, 0.45)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'grid', placeItems: 'center', cursor: 'pointer',
        }}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="var(--t-text)">
            <circle cx="4" cy="10" r="1.5"/><circle cx="10" cy="10" r="1.5"/><circle cx="16" cy="10" r="1.5"/>
          </svg>
        </div>
      </div>

      {/* Swipe dots - środek dolnej części */}
      {total > 1 && total <= 20 && (
        <div style={{
          position: 'absolute',
          bottom: 'calc(280px + env(safe-area-inset-bottom, 0))',
          left: 0, right: 0,
          display: 'flex', justifyContent: 'center', gap: 6,
          zIndex: 15, pointerEvents: 'none',
        }}>
          {photos.map((_, i) => (
            <div key={i} style={{
              width: i === currentIndex ? 18 : 5, height: 5,
              borderRadius: 3,
              background: i === currentIndex ? 'var(--t-text)' : 'rgba(255,255,255,0.35)',
              transition: 'width 250ms ease',
            }} />
          ))}
        </div>
      )}

      {/* Strzałki nawigacyjne (desktop) */}
      {currentIndex > 0 && (
        <button
          onClick={goPrev}
          aria-label="Poprzednie"
          style={{
            position: 'absolute', left: 20, top: '50%',
            transform: 'translateY(-50%)', zIndex: 15,
            width: 48, height: 48, borderRadius: '50%',
            background: 'rgba(0, 0, 0, 0.6)',
            border: 'none', cursor: 'pointer',
            display: 'grid', placeItems: 'center',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12 4l-6 6 6 6" stroke="var(--t-text)" strokeWidth="1.6"
                  strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
      {currentIndex < total - 1 && (
        <button
          onClick={goNext}
          aria-label="Następne"
          style={{
            position: 'absolute', right: 20, top: '50%',
            transform: 'translateY(-50%)', zIndex: 15,
            width: 48, height: 48, borderRadius: '50%',
            background: 'rgba(0, 0, 0, 0.6)',
            border: 'none', cursor: 'pointer',
            display: 'grid', placeItems: 'center',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M8 4l6 6-6 6" stroke="var(--t-text)" strokeWidth="1.6"
                  strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      {/* Bottom editorial info sheet */}
      <div style={{
        position: 'absolute',
        bottom: 'max(env(safe-area-inset-bottom, 0), 20px)',
        left: 0, right: 0, zIndex: 20,
        padding: '0 20px',
      }}>
        <div style={{
          maxWidth: 640, margin: '0 auto',
          padding: '22px 22px 18px',
          borderRadius: 20,
          background: 'rgba(15, 20, 16, 0.9)',
          backdropFilter: 'blur(14px) saturate(140%)',
          WebkitBackdropFilter: 'blur(14px) saturate(140%)',
          border: '1px solid var(--t-line-2)',
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 10,
          }}>
            {tag ? <Meta color="sage">{tag}</Meta> : <Meta>{photo.source_type || 'zdjęcie'}</Meta>}
            {photo.bed_id && (
              <Meta>{photo.plot_name || '—'} · rząd {photo.bed_row_number || '—'}</Meta>
            )}
          </div>

          <Display size={26} style={{
            display: 'block', lineHeight: 1.1, marginBottom: photo.caption ? 8 : 0,
          }}>
            {photo.bed_plant_name ? (
              <>{photo.bed_plant_name}{photo.bed_plant_variety && (
                <> <span style={{ fontStyle: 'italic', color: 'var(--t-sage)' }}>
                  {photo.bed_plant_variety}
                </span></>
              )}</>
            ) : (
              photo.caption || `Zdjęcie z ${formatLocalDate(photo.taken_date || photo.created_at, 'short')}`
            )}
          </Display>

          {photo.caption && photo.bed_plant_name && (
            <div style={{
              fontSize: 13, color: 'var(--t-text-dim)',
              lineHeight: 1.5, marginBottom: 14,
            }}>{photo.caption}</div>
          )}

          {(photo.plot_name || photo.bed_plant_variety || tag) && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
              {photo.plot_name && <Chip tone="sage">{photo.plot_name}</Chip>}
              {photo.bed_plant_variety && <Chip>{photo.bed_plant_variety}</Chip>}
              {tag && <Chip tone="amber">{tag}</Chip>}
            </div>
          )}

          <div style={{
            display: 'flex', gap: 8, alignItems: 'center',
            paddingTop: 14, borderTop: '1px solid var(--t-line-2)',
          }}>
            <div style={{
              flex: 1, display: 'flex', alignItems: 'center', gap: 8,
              color: 'var(--t-text-dim)',
              fontFamily: 'var(--t-mono)', fontSize: 10,
              letterSpacing: '.16em', textTransform: 'uppercase',
            }}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M7 12s-4-2.5-4-6a2.5 2.5 0 014-2 2.5 2.5 0 014 2c0 3.5-4 6-4 6z"
                      stroke="var(--t-rust)" strokeWidth="1.4"
                      fill="var(--t-rust)" fillOpacity=".2" />
              </svg>
              <span>{photo.bed_id ? 'Z dziennika grządki' : 'Quick photo'}</span>
            </div>
            <button
              type="button"
              onClick={async () => {
                try {
                  await axios.post('/api/social/help', { photo_id: photo.id });
                  alert('Pytanie wysłane do znajomych!');
                } catch (e) {
                  alert(e.response?.data?.error || 'Nie udało się wysłać pytania. Spróbuj ponownie.');
                }
              }}
              style={{
                background: 'var(--t-sage)', border: 'none',
                fontFamily: 'var(--t-sans)', fontSize: 11, fontWeight: 500,
                color: 'var(--t-bg)', cursor: 'pointer',
                padding: '6px 10px', borderRadius: 6,
              }}
            >🌱 Zapytaj znajomych</button>
            <button
              type="button"
              onClick={() => setEditing(true)}
              style={{
                background: 'transparent', border: 'none',
                fontFamily: 'var(--t-mono)', fontSize: 10,
                letterSpacing: '.16em', textTransform: 'uppercase',
                color: 'var(--t-sage)', cursor: 'pointer',
                padding: 4,
              }}
            >Edytuj →</button>
          </div>
        </div>
      </div>

      <EditCaptionModal
        open={editing}
        photo={photo}
        onClose={() => setEditing(false)}
        onSaved={refetch}
        onDeleted={() => navigate('/gallery')}
      />
    </div>
  );
}
